import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BarChart3, Loader2, TrendingUp, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EvalChart, LINE_PALETTE } from "@/lib/evalSystem";

const SUMMARY_TONE = {
  "Très satisfaisant": "text-emerald-600",
  "Satisfaisant": "text-sky-600",
  "En progression": "text-emerald-600",
  "Stable": "text-slate-500",
  "En recul": "text-orange-600",
  "À surveiller": "text-rose-600",
  "Pas encore de données": "text-slate-400",
};

export default function ParentSummary() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await api.get("/parent/children");
        setChildren(c.children);
        if (c.children.length) setActiveId(c.children[0].student_id);
        else setLoading(false);
      } catch {
        toast.error("Chargement impossible.");
        setLoading(false);
      }
    })();
  }, []);

  const loadSummary = useCallback(async (sid) => {
    setLoading(true);
    try {
      const { data: s } = await api.get(`/parent/children/${sid}/summary`);
      setData(s);
    } catch {
      toast.error("Chargement du résumé impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeId) loadSummary(activeId);
  }, [activeId, loadSummary]);

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA] dark:bg-[#0A0A0A]">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/85">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate("/parent")}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            data-testid="summary-back" aria-label="Retour"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            <BarChart3 size={18} className="text-slate-400" /> Résumé de l'année
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6">
        {children.length > 1 && (
          <div className="mb-5 flex flex-wrap gap-2" data-testid="summary-child-selector">
            {children.map((c) => (
              <button
                key={c.student_id}
                onClick={() => setActiveId(c.student_id)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  c.student_id === activeId
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "bg-white text-slate-600 border border-slate-200 dark:bg-white/5 dark:text-slate-300"
                }`}
                data-testid={`summary-child-tab-${c.student_id}`}
              >
                {c.first_name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24" data-testid="summary-loading">
            <Loader2 className="animate-spin text-slate-400" size={26} />
          </div>
        ) : !data ? (
          <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500" data-testid="summary-empty">
            Aucune donnée à afficher pour le moment.
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="flex flex-col gap-5"
          >
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100" data-testid="summary-student-name">
                {data.student_name}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">{data.class_name}</p>
            </div>

            {!data.calendar_set ? (
              <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-4 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="calendar-not-set">
                <Info size={16} className="mt-0.5 shrink-0" />
                <span>
                  Les bilans par période s'afficheront dès que l'enseignant aura défini le calendrier scolaire.
                  En attendant, vous pouvez consulter le passeport de la semaine sur la page d'accueil.
                </span>
              </div>
            ) : (
              data.periods.map((p) => (
              <section
                key={p.index}
                className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]"
                data-testid={`period-card-${p.index}`}
              >
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  <TrendingUp size={16} className="text-slate-400" /> {p.label}
                </h3>

                {p.points.length ? (
                  <>
                    <EvalChart data={p.points} criteria={data.criteria} scale={data.chart_scale} height={224} />
                    <div className="mt-4 flex flex-col gap-2.5 border-t border-slate-100 pt-4 dark:border-white/10">
                      {data.criteria.map((c, i) => (
                        <div key={c.id} className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: LINE_PALETTE[i % LINE_PALETTE.length] }} />
                            {c.label}
                          </span>
                          <span className={`text-sm font-medium ${SUMMARY_TONE[p.summary[c.id]] || "text-slate-500"}`} data-testid={`summary-${c.id}-${p.index}`}>
                            {p.summary[c.id]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="py-8 text-center text-sm text-slate-400" data-testid={`period-empty-${p.index}`}>
                    Pas encore de Passeport sur cette période.
                  </p>
                )}
              </section>
            ))
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
